
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { Shield, Mail, Phone, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const WarrantyPolicy = () => {
  return (
    <Layout>
      <Helmet>
        <title>Warranty Policy - 1 Year Limited Warranty Coverage | TrueCore Gadgets India</title>
        <meta name="description" content="TrueCore Gadgets warranty policy: 1-year limited warranty on all devices. Coverage details, claim process, and support contact. Fast resolution for manufacturer defects." />
        <meta name="keywords" content="TrueCore warranty policy, 1 year warranty, device warranty India, warranty claim process, manufacturer defects coverage, tablet warranty, PC warranty" />
        
        <link rel="canonical" href="https://www.truecoregadgets.com/warranty-policy" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Warranty Policy - 1 Year Limited Warranty | TrueCore Gadgets" />
        <meta property="og:description" content="Comprehensive 1-year warranty coverage on all TrueCore devices with fast support for manufacturer defects." />
        <meta property="og:url" content="https://www.truecoregadgets.com/warranty-policy" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4 max-w-4xl">
        <div className="text-center mb-12">
          <Shield className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-bold gradient-text mb-4">Warranty Policy</h1>
          <p className="text-lg text-muted-foreground">
            TrueCore Gadgets provides comprehensive 1-year limited warranty coverage on all devices purchased through our official channels.
          </p>
        </div>

        {/* Warranty Coverage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-xl font-semibold">What's Covered</h2>
            </div>
            <ul className="space-y-3 text-muted-foreground">
              <li>• Manufacturing defects in hardware components</li>
              <li>• Software-related issues and system bugs</li>
              <li>• Display malfunctions and pixel defects</li>
              <li>• Battery performance degradation issues</li>
              <li>• Charging port and connectivity problems</li>
              <li>• Pre-installed software and firmware failures</li>
              <li>• Thermal management system issues</li>
              <li>• Audio and speaker malfunctions</li>
            </ul>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <XCircle className="h-8 w-8 text-red-600 mr-4" />
              <h2 className="text-xl font-semibold">What's Not Covered</h2>
            </div>
            <ul className="space-y-3 text-muted-foreground">
              <li>• Physical damage from drops, impacts, or accidents</li>
              <li>• Liquid damage or water/moisture exposure</li>
              <li>• Unauthorized repairs or modifications</li>
              <li>• Normal wear and tear of components</li>
              <li>• Damage from misuse, abuse, or negligence</li>
              <li>• Third-party accessories and peripherals</li>
              <li>• Damage from power surges or electrical issues</li>
              <li>• Cosmetic damage that doesn't affect functionality</li>
            </ul>
          </div>
        </div>

        {/* Warranty Terms */}
        <div className="bg-primary/10 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <Clock className="h-6 w-6 mr-3" />
            Warranty Duration & Terms
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="font-semibold mb-2">Year Coverage</h3>
              <p className="text-sm text-muted-foreground">Full warranty protection from purchase date</p>
            </div>
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">24</span>
              </div>
              <h3 className="font-semibold mb-2">Hour Response</h3>
              <p className="text-sm text-muted-foreground">Initial response within 24 hours</p>
            </div>
            <div className="text-center">
              <div className="bg-secondary text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">7</span>
              </div>
              <h3 className="font-semibold mb-2">Day Resolution</h3>
              <p className="text-sm text-muted-foreground">Target resolution timeframe</p>
            </div>
          </div>
        </div>

        {/* Claim Process */}
        <div className="bg-muted/50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6">🛠️ How to Raise a Warranty Claim</h2>
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold mb-2">Contact Our Support Team</h3>
                <p className="text-muted-foreground">Email us at <strong>support@truecoregadgets.com</strong> or call <strong>022 6964 7168</strong> with your issue details</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold mb-2">Provide Required Information</h3>
                <p className="text-muted-foreground">Include your order ID, purchase invoice, device serial number, and detailed problem description with photos/videos if applicable</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold mb-2">Initial Assessment</h3>
                <p className="text-muted-foreground">Our technical team will review your case and may provide remote troubleshooting steps or request additional information</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <h3 className="font-semibold mb-2">Resolution</h3>
                <p className="text-muted-foreground">Based on assessment, we will arrange device pickup for repair, provide replacement, or offer other appropriate solutions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-12">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-yellow-800 mb-3">Important Warranty Information</h3>
              <ul className="space-y-2 text-yellow-700">
                <li>• Warranty is valid only with original purchase invoice</li>
                <li>• Device must be purchased from authorized TrueCore channels</li>
                <li>• Warranty coverage begins from the date of delivery</li>
                <li>• Replacement devices carry the remaining warranty period of the original device</li>
                <li>• Data backup is customer's responsibility before sending device for service</li>
                <li>• Warranty may be void if device tamper seals are broken</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Extended Warranty Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <h3 className="text-xl font-bold mb-4">Extended Warranty Plans</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-primary pl-4">
                <h4 className="font-semibold">2-Year Extended Plan</h4>
                <p className="text-sm text-muted-foreground">Additional 1-year coverage beyond standard warranty</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h4 className="font-semibold">3-Year Complete Care</h4>
                <p className="text-sm text-muted-foreground">Extended warranty + accidental damage protection</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Contact our sales team for extended warranty pricing and terms.
            </p>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <h3 className="text-xl font-bold mb-4">On-Site Service</h3>
            <p className="text-muted-foreground mb-4">
              Available for enterprise customers and bulk orders in major cities:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Mumbai Metropolitan Area</li>
              <li>• Delhi NCR Region</li>
              <li>• Bangalore & Hyderabad</li>
              <li>• Pune & Chennai</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              On-site service terms and availability may vary by location.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-card p-8 rounded-lg shadow-md border text-center">
          <h2 className="text-2xl font-bold mb-6">📞 Warranty Support Contact</h2>
          <p className="text-muted-foreground mb-6">
            For warranty claims, technical support, or questions about coverage, reach out to our dedicated warranty team:
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-6">
            <div className="flex items-center space-x-3">
              <Mail className="text-primary" size={20} />
              <span className="font-medium">support@truecoregadgets.com</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="text-primary" size={20} />
              <span className="font-medium">022 6964 7168</span>
            </div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Support Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST<br />
              <strong>Emergency Support:</strong> Available 24/7 for enterprise customers
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarrantyPolicy;
